<template>
  <tr class="tr-user" :class="{'tr-user--cant-edit': !theUserCanManageAssignedHours || isPoolsDeactivated}">
    <th class="tr-user__name">
      <div class="tr-user__name__inner">
        <p>{{ user.name }}</p>
        <div class="tr-user__name__bttn icon__bg-circle hint--right"
             @click="setTheActiveUserInAssignHours"
             :aria-label="$t('vue2.task_overview.task_overview')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="icon-chart tw-icon tw-icon--small">
            <path class="primary"
                  d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2zm11 4a1 1 0 0 0-1 1v8a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1z"/>
            <path class="secondary"
                  d="M8 11a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0v-4a1 1 0 0 1 1-1zm4-2a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0v-6a1 1 0 0 1 1-1z"/>
          </svg>
        </div>
      </div>
    </th>

    <td class="number number_edit td-presence"
        @click.stop="openEdit(user.assigned_total_hours, 'assigned_total_hours', $event)"
        :class="{'is-number--calculated': !isDatesPristine}"
        :content='createTooltip(user.assigned_total_hours)'
        v-if="viewSettings.show_assigned.active"
        v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window',
                onShow: () => !isDatesPristine
              }">
      <span class="icon-clock" v-if="!isDatesPristine"></span>
      {{ user.v_total_hours_assigned_in_period }}
    </td>
    <td class="number td-presence" :aria-label="user.actual_total_hours">
    </td>
    <td class="number td-presence number__scale-indicator" v-if="viewSettings.show_assigned.active"
        :style="scaleIndicator(user.diff_total_hours)"
        :aria-label="user.diff_total_hours">
    </td>

    <td class="number pointer td-rest number__scale-indicator"
        v-if="isPoolsDeactivated"
        :style="scaleIndicator(user.rest_hours)"
        :aria-label="user.rest_hours"
        v-tippy="{
      content: `<div>
                  <div class='do-flex-labels'>Arbejdstid: <span>${user.assigned_total_hours}</span></div>
                  <div class='do-flex-labels'>Tildelte timer: <span>${user.sum_hours}</span></div>
                </div>`,
      allowHTML: true,
      arrow: true,
      placement: 'left'
    }">
    </td>

    <td class="number number_edit td-teaching"
        v-if="viewSettings.show_assigned.active"
        :aria-label="user.assigned_teaching_hours"
        @click.stop="openEdit(user.assigned_teaching_hours, 'assigned_teaching_hours', $event)">
    </td>
    <td class="number td-teaching" :aria-label="user.actual_teaching_hours">
    </td>
    <td class="number number__scale-indicator td-teaching" v-if="viewSettings.show_assigned.active"
        :style="scaleIndicator(user.diff_teaching_hours)"
        :aria-label="user.diff_teaching_hours">
    </td>

    <template v-if="isSchoolYearWithNewEmploymentDeal">
      <td class="number number_edit td-tasks"
          :aria-label="user.assigned_additional_task_hours"
          v-if="viewSettings.show_assigned.active"
          @click.stop="openEdit(user.assigned_additional_task_hours, 'assigned_additional_task_hours', $event)">
      </td>
      <td class="number td-tasks" :aria-label="user.actual_additional_task_hours">
      </td>
      <td class="number number__scale-indicator td-tasks" v-if="viewSettings.show_assigned.active"
          :style="scaleIndicator(user.diff_additional_task_hours)"
          :aria-label="user.diff_additional_task_hours">
      </td>
    </template>

    <template v-if="isSchoolYearWithNewEmploymentDeal">
      <td class="number number_edit td-preparation"
          :aria-label="user.assigned_preparation_hours"
          v-tippy="{
      content: `${user.assigned_teaching_hours} UV-timer * ${prepFactor} forberedelsesfaktor`,
      arrow: true,
      placement: 'top',
      onShow: () => isShowPrepFactorCalculation
    }"
          v-if="viewSettings.show_assigned.active"
          @click.stop="openEdit(user.assigned_preparation_hours, 'assigned_preparation_hours', $event)">
      </td>
      <td class="number td-preparation" :aria-label="user.actual_preparation_hours">
      </td>
      <td class="number number__scale-indicator td-preparation" v-if="viewSettings.show_assigned.active"
          :style="scaleIndicator(user.diff_preparation_hours)"
          :aria-label="user.diff_preparation_hours">
      </td>
    </template>

    <!-- LOKALPULJE -->
    <template v-if="isLocalPoolActivated">
      <td class="number number_edit td-preparation"
          :aria-label="user.assigned_pool_hours"
          v-if="viewSettings.show_assigned.active"
          @click.stop="openEdit(user.assigned_pool_hours, 'assigned_pool_hours', $event)">
      </td>
      <td class="number td-preparation" :aria-label="user.actual_pool_hours">
      </td>
      <td class="number number__scale-indicator td-preparation"
          v-if="viewSettings.show_assigned.active"
          :style="scaleIndicator(user.diff_pool_hours)"
          :aria-label="user.diff_pool_hours">
      </td>
    </template>

    <td class="number pointer td-absence" @click.stop="setActiveUser('absence')">
      <div class="tr-user__text">
        {{ user.actual_absence_hours }}
      </div>
    </td>

    <td class="number number_edit td-sub-hours"
        :aria-label="user.substitute_pool_hours"
        v-if="viewSettings.show_assigned.active"
        @click.stop="openEdit(user.substitute_pool_hours, 'substitute_pool_hours', $event)">
    </td>
    <td class="number pointer td-sub-hours" @click.stop="setActiveUser('substituteHours')"
        :aria-label="user.actual_substitute_hours">
    </td>
    <td class="number number__scale-indicator td-sub-hours"
        v-if="viewSettings.show_assigned.active"
        :style="scaleIndicator(user.diff_substitute_hours)"
        :aria-label="user.diff_substitute_hours">
    </td>
    <template v-if="isSFOActive">
      <td class="number number_edit td-club"
          v-if="viewSettings.show_assigned.active"
          :aria-label="user.assigned_club_hours"
          @click.stop="openEdit(user.assigned_club_hours, 'assigned_club_hours', $event)">
      </td>
      <td class="number td-club" :aria-label="user.actual_club_hours">
      </td>
      <td class="number number__scale-indicator td-club"
          v-if="viewSettings.show_assigned.active"
          :style="scaleIndicator(user.diff_club_hours)"
          :aria-label="user.diff_club_hours">
      </td>
    </template>
  </tr>
</template>

<script>
const I18N = {
  da: {
    tooltip: {
      hours: 'timer',
      days: 'dage',
      work_days: 'arbejdsdage',
      per_day: 'pr. dag',
      calculation: 'Beregning',
      forYear: 'For året:',
      forPeriod: 'For perioden:',
      explanation: '<p><i>Når du vælger et andet datointerval end hele perioden, kan du her se, hvor mange timer, der i gennemsnit skal planlægges for medarbejderen i perioden og per dag. Udregningen er baseret på antal arbejdsdage angivet for personalet, fratrukket weekender, feriedage og helligdage i \'Feriekalender\'</p>'
    }
  },
  nb: {
    tooltip: {
      hours: 'timer',
      days: 'dager',
      per_day: 'per dag',
      work_days: 'arbeidsdager',
      calculation: 'Beregning',
      forYear: 'For året:',
      forPeriod: 'For perioden:',
      explanation: '<p><i>Når du velger et annet datointerval enn hele året, kan du her se, hvor mange timer som skal planlegges for medarbeideren i perioden og i gjennomsnitt per dag. Beregningen trekker helger og ferie- og helligdager fra.</i></p>'
    }
  },
  en: {
    tooltip: {
      hours: 'hours',
      days: 'days',
      per_day: 'per day',
      work_days: 'work days',
      calculation: 'Calculation',
      forYear: 'For the year:',
      forPeriod: 'For the period:',
      explanation: '<p><i>When you choose a different date range than the whole year, you can see here how many hours need to be planned for the employee in the period and on average per day. The calculation subtracts weekends and holidays.</i></p>'
    }
  }
}

export default {
  props: {
    user: {
      type: Object,
      default: {}
    },
    theUserCanManageAssignedHours: {
      type: Boolean,
      default: false
    },
    isSFOActive: {
      type: Boolean,
      default: false
    },
    isSchoolYearWithNewEmploymentDeal: {
      type: Boolean,
      default: false
    },
    isDatesPristine: {
      type: Boolean,
      default: true
    },
    numberOfWorkDaysInPeriod: {
      required: true
    },
    numberOfWorkDays: {
      required: true
    },
    locale: {
      default: 'en'
    },
    isLocalPoolActivated: {
      type: Boolean,
      default: false
    },
    viewSettings: {
      type: Object
    },
    isPoolsDeactivated: {
      type: Boolean,
      default: false
    },
    prepFactor: {
      default: null
    }
  },
  computed: {
    i18n () {
      return I18N[this.locale]
    },
    isShowPrepFactorCalculation () {
      return this.prepFactor !== null && this.isPoolsDeactivated && this.locale === 'da'
    }
  },
  methods: {
    numToFixed (num) {
      return parseFloat(num).toFixed(2)
    },
    createTooltip (sumForYear) {
      const hoursInYear = this.numberOfWorkDays
      const hoursPerDay = sumForYear / hoursInYear
      const hoursForPeriod = hoursPerDay * this.numberOfWorkDaysInPeriod
      const hoursPerDayFixed = this.numToFixed(hoursPerDay)
      const hoursForPeriodFixed = this.numToFixed(hoursForPeriod)
      return `${this.i18n.tooltip.explanation}<br></p><p><b>${this.i18n.tooltip.calculation}</b></p><p>${this.i18n.tooltip.forYear} ${sumForYear} ${this.i18n.tooltip.hours} / ${hoursInYear} ${this.i18n.tooltip.days} = ${hoursPerDayFixed} ${this.i18n.tooltip.hours} ${this.i18n.tooltip.per_day}</p><p>${this.i18n.tooltip.forPeriod} ${this.numberOfWorkDaysInPeriod} ${this.i18n.tooltip.work_days} * ${hoursPerDayFixed} ${this.i18n.tooltip.hours} ${this.i18n.tooltip.per_day} = ${hoursForPeriodFixed} ${this.i18n.tooltip.hours}</p>`
    },
    setTheActiveUserInAssignHours () {
      this.$emit('setTheUserToShowTasks', this.user)
    },
    setActiveUser (filter) {
      let userAndFilter = {
        user: this.user,
        filter: filter
      }
      this.$emit('setActiveUser', userAndFilter)
    },
    openEdit (num, type, evt) {
      if (!this.theUserCanManageAssignedHours || this.isPoolsDeactivated) {
        return
      }
      const numberInfo = {
        userId: this.user.id,
        number: num,
        type: type,
        evt: evt
      }
      this.$emit('openEdit', numberInfo)
    },
    scaleIndicator (diff) {
      let color = 'rgba(62, 207, 142, 0.4)'
      let newDiff = parseFloat(diff)
      let indicatorPercentage = parseFloat(newDiff.toFixed(0))
      if (indicatorPercentage < 10 && indicatorPercentage > 0) {
        indicatorPercentage = '0' + indicatorPercentage
      }
      if (indicatorPercentage > -10 && indicatorPercentage < 0) {
        indicatorPercentage = '0' + (indicatorPercentage * -1)
      }
      if (indicatorPercentage < -9 && indicatorPercentage < 0) {
        indicatorPercentage = indicatorPercentage * -1
      }
      if (newDiff === 0) {
        color = 'transparent'
      }
      if (newDiff < 0) {
        color = 'rgba(250, 86, 90, 0.' + indicatorPercentage + ')'
      }
      if (newDiff < -99) {
        color = 'rgba(250, 86, 90, 1)'
      }
      if (newDiff > 0) {
        color = 'rgba(62, 207, 142, 0.' + indicatorPercentage + ')'
      }
      if (newDiff > 99) {
        color = 'rgba(62, 207, 142, 1)'
      }
      return {'--bgScaleColor': color}
    }
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.tr-user {
  content-visibility: auto;
  contain-intrinsic-size: 1px 50px;
}

.tr-user:hover, .tr-user--active {
  border: 1px solid var(--blue);
  background: #eeeeee !important;
}

.tr-user.tr-user--active th, .tr-user:hover th {
  background: #eeeeee !important;
}

.tr-user th {
  position: sticky;
  z-index: 2;
  left: 0;
  border-collapse: collapse;
  background: #ffffff;
}

.tr-user:nth-child(odd) th {
  background: #f8fafb;
}

.number {
  position: relative;
  padding: 0 16px;
  text-align: right;
  border-bottom: 1px solid #d8d8d8 !important;
}

.number__scale-indicator:before {
  content: attr(aria-label);
  position: absolute;
  font-weight: normal;
  z-index: 0;
  top: 10%;
  left: 10%;
  right: 20%;
  bottom: 10%;
  width: 70%;
  height: 80%;
  background: var(--bgScaleColor);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-radius: 12px;
  padding-right: 10%;
}

.tr-user {
  height: 50px;
  align-items: center;
}

.tr-user__name {
  text-align: left;
  width: 200px;
  border-collapse: collapse;
  border-bottom: 1px solid #d8d8d8;
  padding: 0.5rem 1rem;
}

.tr-user__name__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tr-user__name__inner > p {
  width: 165px;
  overflow: hidden;
  line-height: 1;
}

.tr-user__name__bttn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.number_edit {
  cursor: pointer;
}

.number_edit:hover {
  text-decoration: underline;
  color: var(--blue);
}

.tr-user--cant-edit .number_edit {
  cursor: default;
  text-decoration: none !important;
  color: initial !important;
}

.user-table__body tr td.td-presence,
.user-table__body tr td.td-presence,
.user-table__body tr td.td-presence {
  background: var(--presenceColor);
  border: 0;
}

.user-table__body tr td.td-teaching,
.user-table__body tr td.td-teaching,
.user-table__body tr td.td-teaching {
  background: var(--teachingColor);
  border: 0;
}

.user-table__body tr td.td-tasks,
.user-table__body tr td.td-tasks,
.user-table__body tr td.td-tasks {
  background: var(--taskColor);
  border: 0;
}

.user-table__body tr td.td-preparation,
.user-table__body tr td.td-preparation,
.user-table__body tr td.td-preparation {
  background: var(--prepTimeColor);
  border: 0;
}

.user-table__body tr td.td-absence {
  background: var(--absenceColor);
  border: 0;
}

.user-table__body tr td.td-sub-hours,
.user-table__body tr td.td-sub-hours,
.user-table__body tr td.td-sub-hours {
  background: var(--subColor);
  border: 0;
}

.user-table__body tr td.td-club,
.user-table__body tr td.td-club,
.user-table__body tr td.td-club {
  background: var(--clubColor);
  border: 0;
}

.scale-enter-active {
  transition: transform 0.15s;
}

.scale-enter, .scale-leave-to {
  transform: scale(0.5);
}

.tw-icon {
  width: 1.7rem;
}

.secondary {
  fill: #0D2B3E;
}

.primary {
  fill: #a5b3bb;
}

.number {
  position: relative;
}

.number:before {
  content: attr(aria-label);
  position: absolute;
  font-weight: normal;
  z-index: 0;
  top: 10%;
  left: 10%;
  right: 20%;
  bottom: 10%;
  width: 70%;
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-radius: 12px;
  padding-right: 10%;
}

.is-number--calculated {
  font-style: italic;
}

</style>



// WEBPACK FOOTER //
// AssingHoursAllUsersUserPerf.vue?611ef764